import { useDispatch, useSelector } from 'react-redux';

import { Action } from '@interstate/components/Action';
import { WrenchIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Spinner } from '@interstate/components/Spinner';
import { Box } from '@interstate/components/Box';

import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as repairOrderSelectors from '@makemydeal/dr-dash-store/selectors/repairOrderSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';

import { StyledRepairOrderAlert, StyledRepairOrderCount } from './VehicleInfo.style';

export const RepairOrderAlert = () => {
    const dispatch = useDispatch();
    const theme = useInterstateTheme();
    const isRoAlertEnabled = useSelector(featureToggleSelectors.isRoAlertInMVEnabled);
    const openRepairOrderCount = useSelector(repairOrderSelectors.getRepairOrderAlertCount);
    const isLoading = useSelector(repairOrderSelectors.getRepairOrdersLoading);
    const vin = useSelector(vehicleSelectors.getVehicleVin);
    const make = useSelector(vehicleSelectors.getVehicleMake);
    const model = useSelector(vehicleSelectors.getVehicleModel);
    const year = useSelector(vehicleSelectors.getVehicleYear);

    if (!isRoAlertEnabled) {
        return null;
    }

    // Occupies the widget's slot rather than sitting beside it, so the action row does not reflow
    // when the count arrives and replaces it.
    if (isLoading) {
        return (
            <StyledRepairOrderAlert theme={theme} data-testid="repair-order-alert-loading">
                <Box mt={0.5}>
                    <Spinner size={16} aria-label="Loading repair orders" />
                </Box>
            </StyledRepairOrderAlert>
        );
    }

    if (openRepairOrderCount === 0) {
        return null;
    }

    // The vin travels with the click: this alert sits in a scenario's scope, the drawer it opens does not
    const openRepairOrders = () => {
        dispatch(interactionAnalyticsActionCreators.clickOpenRepairOrdersLink({ vin, make, model, year }));
        dispatch(baseDealActionCreators.setRepairOrdersSlideoutStatus(true, vin));
    };

    return (
        <StyledRepairOrderAlert theme={theme}>
            <Action
                data-testid="repair-order-alert"
                size="sm"
                onClick={openRepairOrders}
                actionIcon={{ icon: <WrenchIcon data-testid="repair-order-alert-icon" />, start: true }}
            >
                RO
            </Action>
            {/* Kept a sibling of the Action rather than a child: inside it, Action's flex layout resizes
                the badge. It carries its own handler so clicking the count opens the drawer too. */}
            <StyledRepairOrderCount theme={theme} data-testid="repair-order-alert-count" onClick={openRepairOrders}>
                {openRepairOrderCount}
            </StyledRepairOrderCount>
        </StyledRepairOrderAlert>
    );
};
